import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { removeLoadingKey } from '../helpers/common';

export const ProfitAndLossTotalNamesLoadingKeys = {
  fetch: 'FETCH_PROFIT_AND_LOSS_TOTAL_NAMES',
  save: 'SAVE_PROFIT_AND_LOSS_TOTAL_NAMES',
};

const defaultSettings = {
  revenue: null,
  contribution_margin: null,
  net_income: null,
  ad_spend: null,
  mer: null,
  beroas: null,
};

const initialState = {
  profitAndLossTotalNames: defaultSettings,
  loading: false,
  loadingKeys: [],
  error: null,
};

export const fetchProfitAndLossTotalNames = createAsyncThunk(
  'profitAndLossTotalNames/fetchProfitAndLossTotalNames',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get('/api/v1/metadata/profit-and-loss-total-names');
      return res.data;
    } catch (error) {
      const message =
        error.response?.data?.error || 'Failed to load Profit & Loss total names settings';
      return rejectWithValue(message);
    }
  }
);

export const saveProfitAndLossTotalNames = createAsyncThunk(
  'profitAndLossTotalNames/saveProfitAndLossTotalNames',
  async (settings, { rejectWithValue }) => {
    try {
      const res = await axios.post('/api/v1/metadata/profit-and-loss-total-names', settings);
      toast.success('Profit & Loss total names saved successfully', { position: 'bottom-right' });
      return res.data;
    } catch (error) {
      const message =
        error.response?.data?.error || 'Failed to save Profit & Loss total names settings';
      toast.error(message, { position: 'bottom-right' });
      return rejectWithValue(message);
    }
  }
);

const profitAndLossTotalNamesSlice = createSlice({
  name: 'profitAndLossTotalNames',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfitAndLossTotalNames.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProfitAndLossTotalNamesLoadingKeys.fetch];
      })
      .addCase(fetchProfitAndLossTotalNames.fulfilled, (state, action) => {
        state.profitAndLossTotalNames = action.payload?.settings || defaultSettings;
        state.loadingKeys = removeLoadingKey(ProfitAndLossTotalNamesLoadingKeys.fetch, state.loadingKeys);
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProfitAndLossTotalNames.rejected, (state, action) => {
        state.error = action.payload || 'Failed to load Profit & Loss total names settings';
        state.profitAndLossTotalNames = defaultSettings;
        state.loadingKeys = removeLoadingKey(ProfitAndLossTotalNamesLoadingKeys.fetch, state.loadingKeys);
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(saveProfitAndLossTotalNames.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProfitAndLossTotalNamesLoadingKeys.save];
      })
      .addCase(saveProfitAndLossTotalNames.fulfilled, (state, action) => {
        state.profitAndLossTotalNames = action.payload?.settings || defaultSettings;
        state.loadingKeys = removeLoadingKey(ProfitAndLossTotalNamesLoadingKeys.save, state.loadingKeys);
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(saveProfitAndLossTotalNames.rejected, (state, action) => {
        state.error = action.payload || 'Failed to save Profit & Loss total names settings';
        state.loadingKeys = removeLoadingKey(ProfitAndLossTotalNamesLoadingKeys.save, state.loadingKeys);
        state.loading = state.loadingKeys.length > 0;
      });
  },
});

export default profitAndLossTotalNamesSlice.reducer;
